import { useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { DataTable } from '../components/common/DataTable';
import { StatusBadge } from '../components/common/StatusBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Plus, Search, MoreHorizontal, Trash2, Route, CheckCircle, Fuel } from 'lucide-react';
import { getTrips, getVehicles, getDrivers, createTrip, completeTrip, deleteTrip } from '../lib/api';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';

export default function TripsPage() {
    const [trips, setTrips] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [completeData, setCompleteData] = useState({ odometer_kembali: '', konsumsi_bbm: '' });
    const [formData, setFormData] = useState({
        vehicle_id: '',
        driver_id: '',
        tanggal: new Date().toISOString().split('T')[0],
        rute_asal: '',
        rute_tujuan: '',
        odometer_berangkat: 0,
        volume_solar_dikirim: 0,
        catatan: ''
    });

    useEffect(() => {
        fetchData();
    }, [statusFilter]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const params = {};
            if (statusFilter !== 'all') params.status = statusFilter;
            
            const [tripsRes, vehiclesRes, driversRes] = await Promise.all([
                getTrips(params),
                getVehicles({ status: 'operasional' }),
                getDrivers({ status: 'aktif' })
            ]);
            setTrips(tripsRes.data);
            setVehicles(vehiclesRes.data);
            setDrivers(driversRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Gagal memuat data');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = () => {
        setFormData({
            vehicle_id: '',
            driver_id: '',
            tanggal: new Date().toISOString().split('T')[0],
            rute_asal: '',
            rute_tujuan: '',
            odometer_berangkat: 0,
            volume_solar_dikirim: 0,
            catatan: ''
        });
        setDialogOpen(true);
    };

    const handleVehicleChange = (vehicleId) => {
        const vehicle = vehicles.find(v => v.id === vehicleId);
        setFormData({ 
            ...formData, 
            vehicle_id: vehicleId,
            odometer_berangkat: vehicle?.odometer || 0,
            driver_id: vehicle?.driver_id || formData.driver_id
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await createTrip(formData);
            toast.success('Trip berhasil dibuat');
            setDialogOpen(false);
            fetchData();
        } catch (error) {
            console.error('Failed to create trip:', error);
            toast.error('Gagal membuat trip');
        }
    };

    const handleCompleteTrip = async () => {
        try {
            await completeTrip(
                selectedTrip.id, 
                parseFloat(completeData.odometer_kembali), 
                parseFloat(completeData.konsumsi_bbm)
            );
            toast.success('Trip selesai');
            setCompleteDialogOpen(false);
            fetchData();
        } catch (error) {
            console.error('Failed to complete trip:', error);
            toast.error('Gagal menyelesaikan trip');
        }
    };

    const handleDelete = async (trip) => {
        if (window.confirm('Yakin ingin menghapus trip ini?')) {
            try {
                await deleteTrip(trip.id);
                toast.success('Trip berhasil dihapus');
                fetchData();
            } catch (error) {
                console.error('Failed to delete trip:', error);
                toast.error('Gagal menghapus trip');
            }
        }
    };

    const filteredTrips = trips.filter(t =>
        t.vehicle_nomor_polisi?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.driver_nama?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.rute_tujuan?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const columns = [
        {
            header: 'Tanggal',
            accessor: 'tanggal',
            render: (value) => <span className="text-sm">{formatDate(value)}</span>
        },
        {
            header: 'Kendaraan',
            accessor: 'vehicle_nomor_polisi',
            render: (value) => (
                <span className="font-mono text-xs uppercase tracking-wider text-primary font-medium">
                    {value}
                </span>
            )
        },
        {
            header: 'Sopir',
            accessor: 'driver_nama',
            render: (value) => <span className="text-sm">{value}</span>
        },
        {
            header: 'Rute',
            accessor: 'rute_asal',
            render: (value, row) => (
                <div>
                    <p className="text-sm">{value}</p>
                    <p className="text-xs text-muted-foreground">→ {row.rute_tujuan}</p>
                </div>
            )
        },
        {
            header: 'Volume',
            accessor: 'volume_solar_dikirim',
            render: (value) => <span className="text-sm">{value?.toLocaleString()} L</span>
        },
        {
            header: 'Jarak',
            accessor: 'jarak_tempuh',
            render: (value) => <span className="text-sm font-mono">{value ? `${value.toLocaleString()} km` : '-'}</span>
        },
        {
            header: 'Efisiensi',
            accessor: 'efisiensi',
            render: (value) => (
                <span className={`text-sm font-mono ${value && value >= 3 ? 'text-green-600' : value ? 'text-orange-600' : ''}`}>
                    {value ? `${value} km/L` : '-'}
                </span>
            )
        },
        {
            header: 'Status',
            accessor: 'status',
            render: (value) => {
                const statusMap = {
                    'dalam_perjalanan': 'in-progress',
                    'selesai': 'completed'
                };
                return <StatusBadge status={statusMap[value] || value} />;
            }
        },
        {
            header: '',
            accessor: 'actions',
            render: (_, row) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`trip-actions-${row.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {row.status === 'dalam_perjalanan' && (
                            <DropdownMenuItem onClick={() => {
                                setSelectedTrip(row);
                                setCompleteData({ 
                                    odometer_kembali: '', 
                                    konsumsi_bbm: '' 
                                });
                                setCompleteDialogOpen(true);
                            }}>
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Selesaikan Trip
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuItem 
                            onClick={() => handleDelete(row)}
                            className="text-destructive focus:text-destructive"
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Hapus
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )
        }
    ];

    return (
        <Layout title="Trip Log">
            <div className="space-y-6" data-testid="trips-page">
                {/* Header Actions */}
                <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                    <div className="flex flex-1 gap-3 w-full sm:w-auto">
                        <div className="relative flex-1 sm:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Cari trip..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 rounded-sm"
                                data-testid="search-trips"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-[160px] rounded-sm" data-testid="status-filter">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Semua Status</SelectItem>
                                <SelectItem value="dalam_perjalanan">Dalam Perjalanan</SelectItem>
                                <SelectItem value="selesai">Selesai</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <Button 
                        onClick={handleOpenDialog} 
                        className="industrial-btn-primary"
                        data-testid="add-trip-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Buat Trip Baru
                    </Button>
                </div>

                {/* Trips Table */}
                <DataTable
                    columns={columns}
                    data={filteredTrips}
                    loading={loading}
                    emptyMessage="Tidak ada trip"
                    emptyIcon={Route}
                />

                {/* Create Trip Dialog */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle className="section-title">Buat Trip Baru</DialogTitle>
                            <DialogDescription>
                                Catat perjalanan pengiriman solar baru.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSubmit}>
                            <div className="grid grid-cols-2 gap-4 py-4">
                                <div className="space-y-2">
                                    <Label htmlFor="vehicle_id">Kendaraan *</Label>
                                    <Select 
                                        value={formData.vehicle_id} 
                                        onValueChange={handleVehicleChange}
                                    >
                                        <SelectTrigger className="rounded-sm" data-testid="trip-vehicle-select">
                                            <SelectValue placeholder="Pilih kendaraan" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {vehicles.map(v => (
                                                <SelectItem key={v.id} value={v.id}>
                                                    {v.nomor_polisi} - {v.merk} ({v.kapasitas_tangki?.toLocaleString()}L)
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="driver_id">Sopir *</Label>
                                    <Select 
                                        value={formData.driver_id} 
                                        onValueChange={(value) => setFormData({ ...formData, driver_id: value })}
                                    >
                                        <SelectTrigger className="rounded-sm" data-testid="trip-driver-select">
                                            <SelectValue placeholder="Pilih sopir" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {drivers.map(d => (
                                                <SelectItem key={d.id} value={d.id}>{d.nama}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="tanggal">Tanggal *</Label>
                                    <Input
                                        id="tanggal"
                                        type="date"
                                        value={formData.tanggal}
                                        onChange={(e) => setFormData({ ...formData, tanggal: e.target.value })}
                                        required
                                        className="rounded-sm"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="odometer_berangkat">Odometer Berangkat (km) *</Label>
                                    <Input
                                        id="odometer_berangkat"
                                        type="number"
                                        value={formData.odometer_berangkat}
                                        onChange={(e) => setFormData({ ...formData, odometer_berangkat: parseFloat(e.target.value) })}
                                        required
                                        className="rounded-sm font-mono"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="rute_asal">Rute Asal *</Label>
                                    <Input
                                        id="rute_asal"
                                        value={formData.rute_asal}
                                        onChange={(e) => setFormData({ ...formData, rute_asal: e.target.value })}
                                        required
                                        className="rounded-sm"
                                        placeholder="Depot Plumpang"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="rute_tujuan">Rute Tujuan *</Label>
                                    <Input
                                        id="rute_tujuan"
                                        value={formData.rute_tujuan}
                                        onChange={(e) => setFormData({ ...formData, rute_tujuan: e.target.value })}
                                        required
                                        className="rounded-sm"
                                        placeholder="SPBU Bekasi"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="volume_solar_dikirim">Volume Solar (Liter) *</Label>
                                    <Input
                                        id="volume_solar_dikirim"
                                        type="number"
                                        value={formData.volume_solar_dikirim}
                                        onChange={(e) => setFormData({ ...formData, volume_solar_dikirim: parseFloat(e.target.value) })}
                                        required
                                        className="rounded-sm"
                                    />
                                </div>
                                <div className="col-span-2 space-y-2">
                                    <Label htmlFor="catatan">Catatan</Label>
                                    <Textarea
                                        id="catatan"
                                        value={formData.catatan}
                                        onChange={(e) => setFormData({ ...formData, catatan: e.target.value })}
                                        className="rounded-sm"
                                        rows={2}
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                                    Batal
                                </Button>
                                <Button type="submit" className="industrial-btn-primary" data-testid="save-trip-btn">
                                    Mulai Trip
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Complete Trip Dialog */}
                <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle className="section-title">Selesaikan Trip</DialogTitle>
                            <DialogDescription>
                                Masukkan data penyelesaian trip untuk {selectedTrip?.vehicle_nomor_polisi}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="py-4 space-y-4">
                            <div className="p-3 bg-muted/50 rounded-sm">
                                <p className="text-sm text-muted-foreground">Odometer Berangkat</p>
                                <p className="text-lg font-bold font-mono">{selectedTrip?.odometer_berangkat?.toLocaleString()} km</p>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="odometer_kembali">Odometer Kembali (km) *</Label>
                                <Input
                                    id="odometer_kembali"
                                    type="number"
                                    value={completeData.odometer_kembali}
                                    onChange={(e) => setCompleteData({ ...completeData, odometer_kembali: e.target.value })}
                                    className="rounded-sm font-mono"
                                    placeholder="Masukkan odometer saat kembali"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="konsumsi_bbm">Konsumsi BBM (Liter) *</Label>
                                <Input
                                    id="konsumsi_bbm"
                                    type="number"
                                    value={completeData.konsumsi_bbm}
                                    onChange={(e) => setCompleteData({ ...completeData, konsumsi_bbm: e.target.value })}
                                    className="rounded-sm"
                                    placeholder="Total BBM yang digunakan"
                                />
                            </div>
                            {completeData.odometer_kembali && completeData.konsumsi_bbm && (
                                <div className="p-3 bg-primary/10 rounded-sm">
                                    <div className="flex justify-between text-sm">
                                        <span>Jarak Tempuh:</span>
                                        <span className="font-mono font-medium">
                                            {(parseFloat(completeData.odometer_kembali) - selectedTrip?.odometer_berangkat).toLocaleString()} km
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-sm mt-1">
                                        <span>Efisiensi:</span>
                                        <span className="font-mono font-medium text-primary">
                                            {((parseFloat(completeData.odometer_kembali) - selectedTrip?.odometer_berangkat) / parseFloat(completeData.konsumsi_bbm)).toFixed(2)} km/L
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setCompleteDialogOpen(false)} className="rounded-sm">
                                Batal
                            </Button>
                            <Button 
                                onClick={handleCompleteTrip}
                                className="industrial-btn-primary"
                                disabled={!completeData.odometer_kembali || !completeData.konsumsi_bbm}
                            >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Selesaikan
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
